import { FormHelperText, Grid, Typography } from '@mui/material';
import React, { useState } from 'react';
import { alpha, styled } from '@mui/material/styles';
import { useDispatch } from 'react-redux';

import SbButton, { SbLoadingButton } from '@Components/Button';
import DragDrop from '@Components/File/DragDrop';
import Modal from '@Components/Modal';
import Image from '@Components/Image';

import { FileResultType } from '../../../Helper/file';
import { readXslAndGetBulkExtraChargesUploadObject } from './helper';
import { BulkExtraChargeShipment, BulkExtraChargesUploadResponse } from './types';
import { downloadSampleExtraChargesSheet } from './downloadSampleExtraChargesSheet';
import API from '@Services/apiAxios';
import { SuccessErrorModalOpen } from '@Reducers/SuccessErrorModal';

interface PropsType {
  open: boolean;
  handleClose: () => void;
}

const bulkExtraChargesUploadAPI = (payload: { shipments: BulkExtraChargeShipment[] }) => {
  return API.put<BulkExtraChargesUploadResponse>('/shipment/admin/bulk-extra-charge', payload);
};

function FileError({ fileError }: { fileError: string }) {
  if (!fileError.trim()) return <></>;

  return (
    <FormHelperText error className="mt-1">
      {fileError}
    </FormHelperText>
  );
}

function BulkExtraChargesUpload(props: PropsType) {
  const [xlsFileData, setXlsFileData] = useState<BulkExtraChargeShipment[] | null>(null);
  const [fileName, setFileName] = useState<string>('');
  const [fileError, setFileError] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState('');
  const dispatch = useDispatch();

  const onDone = async () => {
    if (loading || !xlsFileData) return;

    if (xlsFileData.length > 500) {
      setFileError(
        'You can upload a maximum of 500 shipments at a time. Please reduce the number of shipments and try again.',
      );
      return;
    }

    try {
      setLoading(true);
      setApiError('');

      const payload = {
        shipments: xlsFileData,
      };

      const res = await bulkExtraChargesUploadAPI(payload);
      const data = res?.data;
      const responseMsg =
        data?.msg ||
        `Processing extra charges across ${xlsFileData.length} shipments. You will be notified when it is done.`;

      dispatch(
        SuccessErrorModalOpen({
          type: 'success',
          title: 'Success',
          subTitle: responseMsg,
        }),
      );

      onClose();
    } catch (error: any) {
      const msg =
        error?.response?.data?.msg ||
        error?.response?.data?.message ||
        error?.message ||
        'Bulk Extra Charges upload failed';

      setApiError(msg);
      dispatch(
        SuccessErrorModalOpen({
          type: 'error',
          title: 'Oops!',
          subTitle: msg,
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  const onFileChange = (data: FileResultType[]) => {
    try {
      setFileError('');
      setApiError('');
      setFileName('');

      if (data?.[0]?.fileName) {
        setFileName(data[0].fileName);
      }

      const parsedData = readXslAndGetBulkExtraChargesUploadObject(data);
      setXlsFileData(parsedData);
    } catch (error: any) {
      if (error?.msg) {
        setFileError(error.msg);
        setXlsFileData(null);
      }
    }
  };

  const onDownloadSampleSheet = () => {
    downloadSampleExtraChargesSheet();
  };

  const onClose = () => {
    props.handleClose();
    setFileError('');
    setFileName('');
    setXlsFileData(null);
    setApiError('');
    setLoading(false);
  };

  return (
    <Modal open={props.open} sx={{ minHeight: '470px', bottom: 0 }} maxWidth="sm">
      <Grid container>
        <Grid container item mb={1}>
          <Typography fontWeight="bold" variant="h6">
            Extra Charges Upload
          </Typography>
        </Grid>

        <Grid container item>
          <DragDrop
            readType="xls"
            accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
            subText={fileName ? fileName : 'xlsx or xls file size no more than 10MB'}
            onChange={onFileChange}
          />

          <FileError fileError={fileError || apiError} />
        </Grid>

        <Grid container item mt={2} py={4} justifyContent="center">
          <Grid item xs={12} md="auto">
            <DownloadSampleSheet
              fullWidth
              variant="outlined"
              onClick={onDownloadSampleSheet}
              startIcon={
                <Image
                  src="/images/upload_document.svg"
                  alt="Speedbox"
                  width={35}
                  height={35}
                  style={{ transform: 'rotate(180deg)' }}
                />
              }
            >
              Download sample file
            </DownloadSampleSheet>

            <Typography variant="subtitle2" className="sb-text-gray text-center">
              xlsx or xls file size no more than 10MB
            </Typography>
          </Grid>
        </Grid>

        <Grid container item spacing={2} justifyContent="center">
          <Grid item xs={12} sm={6} md={4}>
            <SbButton variant="outlined" fullWidth onClick={onClose}>
              Cancel
            </SbButton>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <SbLoadingButton
              variant="contained"
              fullWidth
              disabled={!xlsFileData || loading}
              onClick={onDone}
              loading={loading}
            >
              Done
            </SbLoadingButton>
          </Grid>
        </Grid>
      </Grid>
    </Modal>
  );
}

export default BulkExtraChargesUpload;

const DownloadSampleSheet = styled(SbButton)(({ theme }) => ({
  border: `1px solid #EAECF0`,
  borderRadius: '8px !important',
  backgroundColor: alpha(theme.palette.primary.main, 0.03),
  '&:hover': {
    border: `1px solid #EAECF0`,
  },
}));
